import React, { useState, useEffect } from "react";
import Select from "react-select";
import axios from "axios";
import TopNav from "../../components/navigation/TopNav";
import StandardScreen, { ScreenContent } from "../../components/layout/StandardScreen";
import Stack from "../../components/layout/Stack";
import Card from "../../components/ui/Card";
import Button from "../../components/ui/Button";
import FormField from "../../components/forms/FormField";
import StatusMessage from "../../components/ui/StatusMessage";
import { useTranslation } from "react-i18next";

export default function AdminCreateRouteWallet() {
  const { t } = useTranslation();

  const [routeId, setRouteId] = useState(null);
  const [routes, setRoutes] = useState([]);
  const [createdWallets, setCreatedWallets] = useState([]);

  const [message, setMessage] = useState("");
  const [lastAction, setLastAction] = useState("");
  const [loadingRoutes, setLoadingRoutes] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    axios.defaults.headers.common["Authorization"] = `Bearer ${localStorage.getItem("token")}`;
    fetchRoutesWithoutWallets();
  }, []);

  const getErrorMessage = (err, fallbackKey) => {
    const detail = err?.response?.data?.detail;

    if (typeof detail === "string") return detail;

    if (Array.isArray(detail)) {
      return detail
        .map((item) => {
          const location = Array.isArray(item.loc) ? item.loc.join(".") : "";
          return location ? `${location}: ${item.msg}` : item.msg;
        })
        .filter(Boolean)
        .join(" | ");
    }

    return t(fallbackKey);
  };

  const fetchRoutesWithoutWallets = async () => {
    setLoadingRoutes(true);

    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/admin/routes-without-wallets`
      );

      setRoutes(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("❌ Failed to fetch routes without wallets", err);
      setRoutes([]);
      setMessage("❌ " + t("admin_create_route_wallet.messages.fetch_routes_failed"));
      setLastAction("fetch");
    } finally {
      setLoadingRoutes(false);
    }
  };

  const resetForm = () => {
    setRouteId(null);
  };

  const handleSubmit = async () => {
    if (!routeId) {
      setMessage("❌ " + t("admin_create_route_wallet.messages.missing_route"));
      setLastAction("submit");
      return;
    }

    setSubmitting(true);

    try {
      const res = await axios.post(`${process.env.REACT_APP_API_URL}/admin/route-wallets`, {
        route_id: routeId,
      });

      setMessage("✅ " + t("admin_create_route_wallet.messages.success"));
      setLastAction("submit");

      if (res.data?.wallet) {
        setCreatedWallets((prev) => [
          {
            ...res.data.wallet,
            payer: res.data.payer || null,
            route: res.data.route || null,
            bus: res.data.bus || null,
          },
          ...prev,
        ]);
      }

      resetForm();
      fetchRoutesWithoutWallets();
    } catch (err) {
      console.error("❌ Error creating route wallet:", err);
      console.error("BACKEND DETAIL:", err?.response?.data);

      setMessage("❌ " + getErrorMessage(err, "admin_create_route_wallet.messages.failure"));
      setLastAction("submit");
    } finally {
      setSubmitting(false);
    }
  };

  const routeOptions = routes.map((route) => ({
    value: route.route_id,
    label: [
      route.bus_code ? `${t("admin_create_route_wallet.list.bus")} ${route.bus_code}` : null,
      route.route_name,
      route.start_point && route.end_point ? `${route.start_point} → ${route.end_point}` : null,
      route.money_box_payer_userno ? `User ${route.money_box_payer_userno}` : null,
    ]
      .filter(Boolean)
      .join(" | "),
  }));

  const selectedRouteOption =
    routeOptions.find((option) => option.value === routeId) || null;

  const getRouteName = (wallet) => {
    if (wallet?.route?.route_name) return wallet.route.route_name;

    const route = routes.find((r) => r.route_id === wallet.route_id);
    return route?.route_name || "-";
  };

  const getPayerLabel = (wallet) => {
    if (wallet?.payer?.userno) {
      return `${wallet.payer.userno} (${wallet.payer.user_id})`;
    }

    if (wallet?.user_id) {
      return wallet.user_id;
    }

    return "-";
  };

  return (
    <StandardScreen
      background="default"
      topNav={<TopNav title={t("admin_create_route_wallet.title")} mode="main" />}
    >
      <ScreenContent>
        <div style={{ maxWidth: 720, width: "100%", margin: "0 auto" }}>
          <Stack gap="4">
            <Card>
              <Stack gap="4">
                <h1 style={{ margin: 0, fontSize: "1.5rem", fontWeight: 700 }}>
                  {t("admin_create_route_wallet.title")}
                </h1>

                <FormField label={t("admin_create_route_wallet.fields.route.label")}>
                  <Select
                    classNamePrefix="react-select"
                    isLoading={loadingRoutes}
                    options={routeOptions}
                    value={selectedRouteOption}
                    onChange={(option) => {
                      setRouteId(option?.value ?? null);
                      setMessage("");
                      setLastAction("");
                    }}
                    placeholder={t("admin_create_route_wallet.fields.route.placeholder")}
                    noOptionsMessage={() => t("admin_create_route_wallet.fields.route.no_options")}
                  />
                </FormField>

                <Button
                  onClick={handleSubmit}
                  disabled={!routeId || submitting}
                  fullWidth
                >
                  {submitting
                    ? t("admin_create_route_wallet.status.submitting")
                    : lastAction === "submit" && message.startsWith("✅")
                    ? t("admin_create_route_wallet.status.success_short")
                    : lastAction === "submit" && message.startsWith("❌")
                    ? `${t("admin_create_route_wallet.status.error_short")}: ${message.replace("❌ ", "")}`
                    : t("admin_create_route_wallet.actions.submit")}
                </Button>
              </Stack>
            </Card>

            <StatusMessage message={message} />

            <Card>
              <Stack gap="3">
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 12,
                    alignItems: "center",
                  }}
                >
                  <h2 style={{ margin: 0, fontSize: "1.15rem", fontWeight: 700 }}>
                    {t("admin_create_route_wallet.list.title")}
                  </h2>

                  <Button onClick={fetchRoutesWithoutWallets} disabled={loadingRoutes}>
                    {loadingRoutes
                      ? t("admin_create_route_wallet.status.loading")
                      : t("admin_create_route_wallet.actions.refresh_routes")}
                  </Button>
                </div>

                {createdWallets.length === 0 ? (
                  <div style={{ opacity: 0.7 }}>
                    {t("admin_create_route_wallet.list.empty")}
                  </div>
                ) : (
                  <div style={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                      <thead>
                        <tr>
                          <th style={{ textAlign: "left", padding: "8px 0" }}>
                            {t("admin_create_route_wallet.list.columns.walletname")}
                          </th>
                          <th style={{ textAlign: "left", padding: "8px 0" }}>
                            {t("admin_create_route_wallet.list.columns.payer_id")}
                          </th>
                          <th style={{ textAlign: "left", padding: "8px 0" }}>
                            {t("admin_create_route_wallet.list.columns.route")}
                          </th>
                          <th style={{ textAlign: "left", padding: "8px 0" }}>
                            {t("admin_create_route_wallet.list.columns.status")}
                          </th>
                        </tr>
                      </thead>

                      <tbody>
                        {createdWallets.map((wallet) => (
                          <tr key={wallet.wallet_id}>
                            <td style={{ padding: "8px 0", borderTop: "1px solid rgba(0,0,0,0.08)" }}>
                              {wallet.walletname || "-"}
                            </td>

                            <td style={{ padding: "8px 0", borderTop: "1px solid rgba(0,0,0,0.08)" }}>
                              {getPayerLabel(wallet)}
                            </td>

                            <td style={{ padding: "8px 0", borderTop: "1px solid rgba(0,0,0,0.08)" }}>
                              {getRouteName(wallet)}
                            </td>

                            <td style={{ padding: "8px 0", borderTop: "1px solid rgba(0,0,0,0.08)" }}>
                              {wallet.status || "-"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Stack>
            </Card>
          </Stack>
        </div>
      </ScreenContent>
    </StandardScreen>
  );
}